import React from 'react';
import { getPricingInfo, formatUSD } from '../../helpers/filtersAndHelpers';
import { replaceTemplateTags } from '../../helpers/replaceTemplateTags';
import { updateServiceTitanResponse } from '../../helpers/STResponseBuilder';
import './styling.css';

function PricingEstimateBox({ question, branchMetaObj, parentQuestion }) {
  const loggedPricingIdsRef = React.useRef(new Set());

  React.useEffect(() => {
    if (
      question &&
      question.ID &&
      !loggedPricingIdsRef.current.has(question.ID)
    ) {
      // Build ServiceTitan payload
      const serviceTypeKey = question['Service Type'] || '';
      let pricingInfo = null;
      let pricingValue = '';
      if (serviceTypeKey) {
        pricingInfo = getPricingInfo(branchMetaObj, serviceTypeKey);
        if (pricingInfo && pricingInfo.fixedPrice) {
          pricingValue = pricingInfo.fixedPrice;
        } else if (
          pricingInfo &&
          pricingInfo.priceLow &&
          pricingInfo.priceHigh
        ) {
          pricingValue = `${formatUSD(parseFloat(pricingInfo.priceLow))} - ${formatUSD(parseFloat(pricingInfo.priceHigh))}`;
        }
      }
      const payload = {
        questionId: question.ID,
        value: pricingValue,
        questionText: question.Text || '',
        type: 'Pricing',
        selectedLabel: question.Text || '',
        questionObj: question,
        event: 'PricingDisplay',
        pagePostType: 'frontpage',
        pagePostType2: 'single-page',
        pagePostAuthor: 'nearu',
        schedulerName: question.formType || '',
        pricing: pricingValue,
      };
      if (window && window.__stResponseObj) {
        window.__stResponseObj = updateServiceTitanResponse(
          window.__stResponseObj,
          payload
        );
      }
      // NOTE: Do NOT write to window.__stSimpleAnswers here.
      // PricingEstimateBox renders for every service on the screen simultaneously,
      // so writing here would include all displayed prices, not just the selected one.
      // The selected service's pricing is written by EnterpriseTool via updateSimpleAnswers
      // after the user makes their selection.
      loggedPricingIdsRef.current.add(question.ID);
    }
  }, [question, branchMetaObj]);

  try {
    if (!question) {
      return (
        <div className="pricing-estimate-error">
          Error: Missing question prop in PricingEstimateBox.
        </div>
      );
    }

    let displayText = question?.Text || '';

    // Extract all template tags from the text
    const templateRegex = /{{\s*([^}]+)\s*}}/g;
    const matches = [...displayText.matchAll(templateRegex)];

    // Process each template tag
    for (const match of matches) {
      const fullMatch = match[0];
      const key = match[1].trim();

      if (key === 'SERVICE_NAME') {
        // Replace with parent question's text
        const serviceName =
          (parentQuestion && parentQuestion.Text) || 'this service';
        displayText = displayText.replace(fullMatch, serviceName);
      } else if (key === 'PHONE_NUMBER') {
        // Replace with phone number from branchMetaObj
        const phoneNumber = replaceTemplateTags(
          '{{PHONE_NUMBER}}',
          branchMetaObj || {}
        );
        displayText = displayText.replace(
          fullMatch,
          phoneNumber || '(910) 000-0000'
        );
      } else {
        // Assume it's a pricing key - use it as-is (already hyphenated)
        if (branchMetaObj) {
          const pricingInfo = getPricingInfo(branchMetaObj, key);
          let priceStr = '';

          // Use the full description (with prefix)
          if (pricingInfo && pricingInfo.description) {
            priceStr = pricingInfo.description;
          } else if (pricingInfo.fixedPrice) {
            priceStr = pricingInfo.fixedPrice;
          } else if (pricingInfo.priceLow && pricingInfo.priceHigh) {
            priceStr = `${formatUSD(pricingInfo.priceLow)} - ${formatUSD(pricingInfo.priceHigh)}`;
          } else {
            priceStr = 'not available';
          }

          // Replace with bold price, ensuring it always sits on its own
          // paragraph line regardless of how the tag was authored in Airtable
          // (some records have \n\n around the tag, others have it inline).
          // First strip any existing newlines immediately surrounding the tag
          // so we never end up with \n\n\n\n, then wrap with exactly one \n\n.
          displayText = displayText.replace(
            /\n*\s*{{[^}]+}}\s*\n*/g,
            `\n\n<strong>${priceStr}</strong>\n\n`
          );
        }
      }
    }

    // Split on double-newlines (paragraph breaks authored in Airtable) to
    // produce separate <div> blocks. Single \n within a paragraph is preserved
    // as a <br>. All punctuation from the source text is kept exactly as-is —
    // we never strip or add periods here.
    const paragraphs = displayText
      .split(/\n{2,}/)
      .map((p) => p.trim().replace(/\n/g, '<br>'))
      .filter((p) => p.length > 0);

    return (
      <div className="pricing-estimate-box">
        {paragraphs.map((paragraph, index) => (
          <div
            key={index}
            className="pricing-estimate-main"
            style={{ marginTop: index > 0 ? '1em' : '0' }}
            dangerouslySetInnerHTML={{ __html: paragraph }}
          />
        ))}
      </div>
    );
  } catch (err) {
    return (
      <div className="pricing-estimate-error">
        Error rendering PricingEstimateBox: {err && err.toString()}
      </div>
    );
  }
}

export default PricingEstimateBox;
